<template>
  <div class="flex h-screen bg-[#f3f4f8]">
    <!-- Sidebar Component -->
    <SidebarNavigation :isOpen="sidebarOpen" @close="sidebarOpen = false" />
    <!-- Overlay for mobile when sidebar open -->
    <div v-if="sidebarOpen" class="fixed inset-0 bg-black bg-opacity-40 z-20 md:hidden" @click="sidebarOpen = false">
    </div>


    <!-- Main Content -->
    <div class="flex-1 overflow-x-hidden overflow-y-auto animate-fadeIn relative z-10 transition-all duration-300">
      <!-- Header -->
      <header class="bg-white shadow-sm sticky top-0 z-30">
        <div class="px-6 py-4 flex flex-col md:flex-row md:items-center md:justify-between">
          <!-- Left: Burger + Title -->
          <div class="flex items-center  md:justify-start w-full md:w-auto">
            <!-- Mobile Burger -->
            <button @click="sidebarOpen = !sidebarOpen" class="md:hidden text-[#4d148c] focus:outline-none mr-3">
              <i class="fas fa-bars text-2xl"></i>
            </button>

            <!-- Header Text -->
            <div class="flex flex-col">
              <div class="flex items-center space-x-2 text-sm text-gray-500">
                <router-link to="/dashboard" class="hover:text-[#4d148c]">Dashboard</router-link>
                <i class="fas fa-chevron-right text-xs"></i>
                <span class="text-[#4d148c] font-medium">All Items</span>
              </div>
              <h2 class="text-xl font-semibold text-gray-800 mt-1">All Items</h2>
            </div>
          </div>

          <!-- Right: Desktop button -->
          <div class="hidden md:block">
            <router-link to="/post-item"
              class="px-4 py-2 bg-[#4d148c] text-white rounded-lg hover:bg-[#6d2ba8] transition-colors duration-300 flex items-center">
              <i class="fas fa-plus mr-2"></i>
              Post New Item
            </router-link>
          </div>
        </div>
      </header>

      <!-- Filters -->
      <div class="container mx-auto px-4 py-6">
        <div class="bg-white rounded-xl shadow-sm p-4 mb-6">
          <!-- Mobile Post Button -->
          <div class="flex justify-end mb-4 md:hidden">
            <router-link to="/post-item"
              class="px-4 py-2 bg-[#4d148c] text-white rounded-lg hover:bg-[#6d2ba8] transition-colors duration-300 flex items-center">
              <i class="fas fa-plus mr-2"></i> Post New Item
            </router-link>
          </div>

          <!-- Filter Grid -->
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            <!-- Status -->
            <div>
              <label class="block text-sm text-gray-500 mb-1">Status</label>
              <select v-model="filters.status"
                class="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#4d148c] focus:border-transparent">
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="ready_to_pickup">Ready to Pickup</option>
                <option value="in_process">In Process</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            <!-- Category -->
            <div>
              <label class="block text-sm text-gray-500 mb-1">Category</label>
              <select v-model="filters.category"
                class="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#4d148c] focus:border-transparent">
                <option value="">All Categories</option>
                <option value="electronics">Electronics</option>
                <option value="clothing">Clothing</option>
                <option value="documents">Documents</option>
                <option value="food">Food</option>
                <option value="other">Other</option>
              </select>
            </div>

            <!-- Search -->
            <div class="sm:col-span-2 lg:col-span-1">
              <label class="block text-sm text-gray-500 mb-1">Search</label>
              <input v-model="filters.search" type="text" placeholder="Search items..."
                class="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#4d148c] focus:border-transparent">
            </div>
          </div>
        </div>

        <!-- Items Table -->
        <div class="bg-white rounded-xl shadow-sm overflow-x-auto">
          <!-- Loading -->
          <div v-if="loading" class="p-8 text-center">
            <i class="fas fa-circle-notch fa-spin text-2xl text-[#4d148c]"></i>
            <p class="mt-2 text-gray-500">Loading items...</p>
          </div>

          <!-- Error -->
          <div v-else-if="error" class="p-6">
            <div class="bg-red-50 text-red-600 p-4 rounded-lg flex items-center">
              <i class="fas fa-exclamation-circle mr-2"></i>{{ error }}
            </div>
          </div>

          <!-- Empty -->
          <div v-else-if="items.length === 0" class="p-8 text-center">
            <div class="inline-block p-4 rounded-full bg-gray-50 mb-4">
              <i class="fas fa-box-open text-2xl text-gray-400"></i>
            </div>
            <p class="text-gray-500 mb-4">No items found</p>
            <router-link to="/post-item"
              class="inline-flex items-center px-4 py-2 bg-[#4d148c] text-white rounded-lg hover:bg-[#6d2ba8] transition-colors duration-300">
              <i class="fas fa-plus mr-2"></i> Post New Item
            </router-link>
          </div>

          <!-- Table -->
          <table v-else class="w-full table-auto min-w-[900px]">
            <thead class="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item ID</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Approval</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">From</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">To</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Deliverers
                </th>
                <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Admin
                  Request</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              <tr v-for="item in items" :key="item.id" class="hover:bg-gray-50 transition-colors duration-200">
                <td class="px-4 py-4 whitespace-nowrap">
                  <div class="flex items-center">
                    <div class="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center mr-3">
                      <i class="fas fa-box text-[#4d148c]"></i>
                    </div>
                    <div>
                      <div class="text-sm font-medium text-gray-900">{{ item.name }}</div>
                      <div class="text-sm text-gray-500">Qty: {{ item.quantity }}</div>
                    </div>
                  </div>
                </td>
                <td class="px-6 py-4 text-left max-w-[150px] truncate text-sm text-gray-500 flex items-center">
                  <span class="truncate" :title="item.id">{{ item.id }}</span>
                  <button @click="copyToClipboard(item.id)" class="text-blue-500 hover:text-blue-700 ml-1">
                    <i class="fas fa-copy text-sm"></i>
                  </button>
                </td>
                <td class="px-4 py-4 whitespace-nowrap">
                  <span :class="getStatusClass(item.status)">{{ formatStatus(item.status) }}</span>
                </td>
                <td class="px-4 py-4 whitespace-nowrap">
                  <span class="px-2 py-1 rounded text-xs font-medium"
                    :class="item.sending_approval === 'approved' ? 'bg-green-100 text-green-600' : item.sending_approval === 'pending' ? 'bg-yellow-100 text-yellow-600' : 'bg-red-100 text-red-800'">
                    {{ item.sending_approval }}
                  </span>
                </td>
                <td class="px-4 py-4 whitespace-nowrap">
                  <span class="px-3 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800 capitalize">{{
                    item.category }}</span>
                </td>
                <td class="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{{ item.from_location }}</td>
                <td class="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{{ item.to_location }}</td>
                <td class="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{{ formatDateTime(item.created_at) }}</td>
                <td class="px-4 py-4 whitespace-nowrap text-sm">
                  <div class="flex items-center space-x-1">
                    <span
                      :class="['px-2 py-1 rounded-full text-xs', item.delivery_approval === 'approved' && item.request_status === 'pending' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700']">
                      {{ item.delivery_approval === 'approved' && item.request_status === 'pending' ? 1 : 0 }}
                    </span>
                    <i class="fas fa-users text-gray-400"></i>
                  </div>
                </td>
                <td class="px-4 py-4 whitespace-nowrap text-sm text-center">
                  <template v-if="item.info_request_to_sender">
                    <button @click="openDialog(item)">
                      <span
                        class="inline-flex items-center px-4 py-1.5 rounded-full text-xs font-semibold shadow transition-all duration-300"
                        :class="[item.info_request_to_sender?.sender_response ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white group-hover:scale-105' : 'bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 text-white animate-pulse group-hover:scale-105']">
                        <i
                          :class="['fas mr-2 transition-all', item.info_request_to_sender?.sender_response ? 'fa-check-circle' : 'fa-exclamation-triangle']"></i>
                        {{ item.info_request_to_sender?.sender_response ? 'Information Submitted' : 'Action Required' }}
                      </span>
                    </button>
                  </template>
                  <template v-else>
                    <span class="text-gray-400 text-xs">—</span>
                  </template>
                </td>


                <td class="px-4 py-4 whitespace-nowrap text-sm">
                  <router-link :to="`/items/${item.id}`"
                    class="text-[#4d148c] hover:text-[#6d2ba8] transition-colors duration-300 flex items-center group">
                    View Details
                    <i
                      class="fas fa-chevron-right ml-1 text-xs group-hover:translate-x-1 transition-transform duration-300"></i>
                  </router-link>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

      </div>

      <!-- Pagination -->
      <div
        class="sticky bottom-0 bg-white px-4 border-t border-gray-100 flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
        <div class="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-4">
          <select v-model="pagination.perPage" @change="loadItems(1, true)"
            class="px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#4d148c] focus:border-transparent">
            <option :value="10">10 per page</option>
            <option :value="25">25 per page</option>
            <option :value="50">50 per page</option>
          </select>
          <span class="text-sm text-gray-500">Showing {{ paginationInfo }}</span>
        </div>
        <div class="flex items-center space-x-2">
          <button v-if="pagination.currentPage > 1" @click="loadItems(pagination.currentPage - 1, true)"
            class="px-4 py-2 text-sm text-[#4d148c] hover:bg-gray-50 rounded-lg transition-colors duration-300">
            <i class="fas fa-chevron-left mr-1"></i> Previous
          </button>
          <button v-if="hasNextPage" @click="loadItems(pagination.currentPage + 1, true)"
            class="px-4 py-2 text-sm text-[#4d148c] hover:bg-gray-50 rounded-lg transition-colors duration-300">
            Next
            <i class="fas fa-chevron-right ml-1"></i>
          </button>
        </div>
      </div>


      <!-- Admin Requested More Information Card -->
      <div v-if="showUserResponseDialog"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 transition-all duration-300">
        <div
          class="relative w-full max-w-3xl mx-4 md:mx-auto rounded-2xl p-8 pt-12 border-l-[6px] border-amber-500 bg-white shadow-2xl transform transition-all duration-300 hover:scale-[1.01">
          <!-- Cancel Button -->
          <button @click="showUserResponseDialog = false"
            class="absolute top-3 right-3 text-gray-500 hover:text-red-600 transition-all text-xl">
            <i class="fas fa-times"></i>
          </button>

          <!-- Header -->
          <div class="flex items-center mb-6">
            <i :class="userResponseData.response_description ? 'fas fa-check-circle text-green-600' : 'fas fa-exclamation-triangle text-amber-600'"
              class="mr-3 text-2xl"></i>
            <h3 :class="userResponseData.response_description ? 'text-green-700' : 'text-amber-700'"
              class="text-xl font-bold">
              {{
                userResponseData.response_description
                  ? 'Information Submitted'
                  : 'Action Required: Admin Requested Information'
              }}
            </h3>
          </div>


          <!-- Info Grid -->
          <div class="mb-6">
            <!-- Admin Message -->
            <div class="mb-6">
              <label class="block text-sm font-semibold text-gray-500 mb-1">Admin Message</label>
              <div class="bg-amber-50 text-gray-800 font-medium rounded-lg px-4 py-3 border border-amber-200 shadow-sm">
                {{ userResponseData.description }}
              </div>

              <!-- Requested File -->
              <template v-if="userResponseData.request_file_path">
                <label class="block text-sm font-semibold text-gray-500 mt-4 mb-1">Requested File</label>
                <button @click="viewProofOfTravel(userResponseData.request_file_path)"
                  class="text-amber-700 font-semibold underline hover:text-amber-800 transition-all">
                  View Request File
                </button>
              </template>
            </div>
          </div>

          <!-- Response Section -->
          <div v-if="userResponseData.response_description"
            class="bg-gray-50 border border-amber-100 rounded-xl p-5 shadow-sm">
            <label class="block text-sm font-semibold text-gray-500 mb-1">Your Response</label>
            <p class="text-gray-800 font-medium mb-2">
              {{ userResponseData.response_description }}
            </p>
            <p class="text-xs text-gray-500 mb-2">
              Submitted on {{ formatDateTime(userResponseData.response_at) }}
            </p>

            <!-- Response File -->
            <template v-if="userResponseData.response_file_path">
              <label class="block text-sm font-semibold text-gray-500 mt-3 mb-1">Response File</label>
              <button @click="viewProofOfTravel(userResponseData.response_file_path)"
                class="text-amber-700 font-semibold underline hover:text-amber-800 transition-all">
                View Response File
              </button>
            </template>
          </div>


          <!-- Respond / Resubmit Button -->
          <div class="mt-6 flex justify-end" v-if="modelItem.sending_approval === 'pending'">
            <button @click="openResponseDialog"
              class="px-4 py-2 rounded-md bg-amber-600 text-white hover:bg-amber-700 transition-all">
              <i class="fas fa-reply mr-2"></i>
              {{ userResponseData.response_description ? 'Resubmit Response' : 'Respond to Request' }}
            </button>
          </div>

          <!-- More Info Dialog -->
          <dialog ref="moreInfoDialog" class="rounded-xl p-6 sm:p-8 bg-white w-full md:max-w-md shadow-xl border relative">
            <!-- Header -->
            <div class="flex justify-between items-center mb-4">
              <h3 class="text-lg sm:text-xl font-semibold text-[#4d148c]">Submit Your Response</h3>
              <button @click="closeResponseDialog"
                class="absolute top-3 right-3 text-gray-500 hover:text-red-600 transition-all text-xl">
                <i class="fas fa-times"></i>
              </button>
            </div>

            <!-- Form content -->
            <div class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <textarea v-model="moreInfoMessage" rows="4"
                  class="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-[#4d148c]"
                  placeholder="Write your message..."></textarea>
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Attach Proof (optional)</label>
                <input type="file" @change="handleFileChange"
                  class="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-[#4d148c] file:text-white hover:file:bg-[#3a0e6c]" />
              </div>
            </div>

            <!-- Actions -->
            <div class="mt-6 flex flex-col sm:flex-row justify-center sm:justify-end gap-3">
              <button @click="closeResponseDialog"
                class="px-4 py-2 rounded-md border border-gray-400 text-gray-700 hover:bg-gray-100 w-full sm:w-auto">
                Cancel
              </button>
              <button @click="submitMoreInfoRequest"
                class="px-4 py-2 rounded-md bg-amber-500 text-white hover:bg-amber-600 w-full sm:w-auto">
                Send Response
              </button>
            </div>
          </dialog>

        </div>
      </div>

      <!-- Proof of Travel Modal -->
      <div v-if="showProofModal" class="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog"
        aria-modal="true">
        <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">

          <!-- Background overlay -->
          <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" @click="closeProofModal">
          </div>

          <!-- Modal panel -->
          <div
            class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
            <div class="absolute top-0 right-0 pt-4 pr-4">
              <button @click="closeProofModal" class="text-gray-400 hover:text-gray-500 focus:outline-none">
                <i class="fas fa-times"></i>
              </button>
            </div>

            <!-- Modal Content -->
            <div class="sm:flex sm:items-start">
              <div class="mt-3 text-center sm:mt-0 sm:text-left w-full">
                <h3 class="text-lg leading-6 font-medium text-gray-900">
                  Proof of Travel Document
                </h3>

                <div class="mt-4">
                  <!-- Loading State -->
                  <div v-if="proofLoading" class="flex justify-center py-8">
                    <i class="fas fa-circle-notch fa-spin text-2xl text-[#4d148c]"></i>
                  </div>

                  <!-- Error State -->
                  <div v-else-if="proofError" class="bg-red-50 text-red-600 p-4 rounded-lg">
                    {{ proofError }}
                  </div>

                  <!-- Document Preview -->
                  <div v-else-if="proofDocument" class="rounded-lg overflow-hidden">
                    <object :data="proofDocument.url" type="application/pdf" class="w-full h-[600px]">
                      <embed :src="proofDocument.url" type="application/pdf" class="w-full h-[600px]">
                      <p class="text-center py-4 text-gray-600">
                        Unable to display PDF. <a :href="proofDocument.url" target="_blank"
                          class="text-[#4d148c] hover:underline">Click here to view</a>
                      </p>
                    </object>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '../services/api';
import SidebarNavigation from '@/components/Sidebar.vue';
import debounce from 'lodash/debounce';
import { useToast } from 'vue-toastification';

export default {
  name: 'ItemsListPage',
  components: {
    SidebarNavigation
  },
  data() {
    return {
      items: [],
      loading: false,
      error: null,
      filters: {
        status: '',
        category: '',
        search: '',
        per_page: 10
      },
      pagination: {
        currentPage: 1,
        perPage: 10,
        total: 0
      },
      sortBy: 'created_at',
      sortOrder: 'desc',
      userInitials: '',
      deliverers: {},
      deliveringLoading: false,

      userResponseData: {
        description: "",
        request_file_path: null,
        response_description: "",
        response_file_path: null,
        response_at: null,
        item_id: null
      },
      showUserResponseDialog: false,
      showProofModal: false,
      proofLoading: (false),
      proofError: '',
      proofDocument: null,

      //Reponse request Dialog
      moreInfoMessage: '',
      moreInfoFile: null,
      modelItem: null,
      intervalId: null,
      sidebarOpen: false
    }
  },
  computed: {
    paginationInfo() {
      const start = (this.pagination.currentPage - 1) * this.pagination.perPage + 1;
      const end = Math.min(start + this.pagination.perPage - 1, this.pagination.total);
      return `${start}-${end} of ${this.pagination.total}`;
    },
    hasNextPage() {
      return this.pagination.currentPage < Math.ceil(this.pagination.total / this.pagination.perPage);
    }
  },
  methods: {
    setUserInfo() {
      const user = JSON.parse(localStorage.getItem('user'));
      this.userName = user.name;
      this.userInitials = user.name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase();
    },
    async loadItems(page = 1, showLoading) {
      try {
        if (showLoading) {
          this.loading = true;
        }
        this.error = null;

        const params = {
          page,
          per_page: this.pagination.perPage,
          sort_by: this.sortBy,
          sort_order: this.sortOrder,
        };

        // Only add non-empty filters
        if (this.filters.status) {
          params.status = this.filters.status;
        }
        if (this.filters.category) {
          params.category = this.filters.category;
        }
        if (this.filters.search?.trim()) {
          params.search = this.filters.search.trim();
        }

        const response = await api.get('/api/items', { params });

        if (response.data.success) {
          this.items = response.data.data.items;
          this.pagination = {
            currentPage: response.data.data.pagination.current_page,
            perPage: response.data.data.pagination.per_page,
            total: response.data.data.pagination.total
          };
        } else {
          throw new Error(response.data.message || 'Failed to load items');
        }
      } catch (error) {
        console.error('Error loading items:', error);
        this.error = error.response?.data?.message || error.message || 'Failed to load items';
      } finally {
        if (showLoading) {
          this.loading = false;
        }
      }
    },

    sort(field) {
      if (this.sortBy === field) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortBy = field;
        this.sortOrder = 'asc';
      }
      this.loadItems(1, true);
    },
    applyFilters() {
      if (this.filters.search) {
        // Use debounced version for search
        this.debouncedLoadItems(1);
      } else {
        // Immediate load for other filters
        this.loadItems(1, true);
      }
    },
    formatDateTime(dateString) {
      return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    getStatusClass(status) {
      return {
        pending: 'px-3 py-1 text-xs font-medium rounded-full bg-yellow-50 text-yellow-800',
        'in_process': 'px-3 py-1 text-xs font-medium rounded-full bg-blue-50 text-blue-800',
        delivered: 'px-3 py-1 text-xs font-medium rounded-full bg-green-50 text-green-800',
        'ready_to_pickup': 'px-3 py-1 text-xs font-medium rounded-full bg-purple-50 text-purple-800',
        cancelled: 'px-3 py-1 text-xs font-medium rounded-full bg-red-50 text-red-800'
      }[status.toLowerCase()] || '';
    },
    formatStatus(status) {
      if (!status) return '';
      return status
        .toLowerCase()
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    },
    getSortIconClass(field) {
      if (this.sortBy !== field) return 'text-gray-400';
      return {
        asc: 'text-[#4d148c] fa-sort-up',
        desc: 'text-[#4d148c] fa-sort-down'
      }[this.sortOrder];
    },
    async loadDeliverers() {
      try {
        this.deliveringLoading = true;
        const response = await api.get('/api/get-all-deliverers');

        if (response.data.success) {
          // Convert array to object for O(1) lookup
          this.deliverers = response.data.data.reduce((acc, deliverer) => {
            acc[deliverer.id] = deliverer.name;
            console.log(acc);
            return acc;
          }, {});
        }
      } catch (error) {
        console.error('Error loading deliverers:', error);
        this.error = 'Failed to load deliverer information';
      } finally {
        this.deliveringLoading = false;
      }
    },
    getInitials(name) {
      if (!name) return '...';
      return name
        .split(' ')
        .map(word => word[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    },
    openDialog(item) {
      this.modelItem = item;
      this.showUserResponseDialog = true;

      this.userResponseData = {
        description: item.info_request_to_sender?.description,
        request_file_path: item.info_request_to_sender?.request_file_path,
        response_description: item.info_request_to_sender?.sender_response || "",
        response_file_path: item.info_request_to_sender?.response_file_path || null,
        response_at: item.info_request_to_sender?.response_at || null,
        item_id: item.id
      };
    },

    async viewProofOfTravel(file_path) {
      this.showProofModal = true;
      this.proofLoading = true;
      this.proofError = null;
      this.proofDocument = null;

      try {
        // Create blob URL for the streamed response
        const response = await api.get(`/api/proof-of-travel/${file_path}`, {
          responseType: 'blob'  // Important: Set responseType to 'blob'
        });

        // Create blob URL
        const blob = new Blob([response.data], {
          type: response.headers['content-type']
        });
        const url = URL.createObjectURL(blob);

        this.proofDocument = {
          url: url,
          type: response.headers['content-type']?.includes('pdf') ? 'pdf' : 'image'
        };
      } catch (error) {
        this.proofError = error.response?.data?.message || error.message || 'Failed to load proof of travel document';
        console.error('Error loading proof of travel:', error);
      } finally {
        this.proofLoading = false;
      }
    },
    closeProofModal() {
      this.showProofModal = false
      this.proofDocument = null
      this.proofError = ''
    },

    openResponseDialog() {
      this.moreInfoMessage = '';
      this.selectedFile = null;
      const dialog = this.$refs.moreInfoDialog;
      if (dialog) {
        if (dialog.open) dialog.close(); // ensure clean open
        dialog.showModal();
      }
    },
    closeResponseDialog() {
      const dialog = this.$refs.moreInfoDialog;
      if (dialog && dialog.open) dialog.close();
      this.moreInfoMessage = '';
      this.selectedFile = null;
    },
    handleFileChange(event) {
      const file = event.target.files[0];
      this.selectedFile = file;
    },
    async submitMoreInfoRequest() {
      const toast = useToast();
      const formData = new FormData();
      formData.append('item_id', this.userResponseData.item_id);
      formData.append('description', this.moreInfoMessage);
      formData.append('response_by', 'sender'); // Assuming 'sender' is the user type
      if (this.selectedFile) {
        formData.append('file', this.selectedFile);
      }

      try {
        await api.post('/api/respond-further-info', formData);
        toast.success('Information sent successfully!');
        this.closeResponseDialog();
        this.showUserResponseDialog = false;
        this.loadItems(1, true)
        // Optional: reload or refetch item
      } catch (error) {
        console.error(error);
        toast.error('Failed to send information.');
      }
    },
    handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        this.loadItems(undefined, false)
      }
    },
    copyToClipboard(text) {
      const toast = useToast()
      navigator.clipboard.writeText(text).then(() => {
        toast.success('Copied to clipboard!');
      });
    }
  },
  created() {
    this.loadItems(undefined, true);
    this.setUserInfo();
    this.loadDeliverers();
    // Create debounced version of loadItems
    this.debouncedLoadItems = debounce(this.loadItems, 500);
  },
  mounted() {
    // Poll every 30 seconds, but only if tab is active
    this.intervalId = setInterval(() => {
      if (document.visibilityState === 'visible') {
        this.loadItems(undefined, false)
      }
    }, 30000)

    document.addEventListener('visibilitychange', this.handleVisibilityChange)
  },


  watch: {
    'filters.status'() {
      this.loadItems(1, true);
    },
    'filters.category'() {
      this.loadItems(1, true);
    },
    'filters.search'() {
      if (this.filters.search) {
        this.debouncedLoadItems(1);
      } else {
        this.loadItems(1, true);
      }
    },
    'filters.per_page'() {
      this.loadItems(1, true);
    }
  },
  beforeUnmount() {
    if (this.debouncedLoadItems) {
      this.debouncedLoadItems.cancel();
    }

    // Clean up
    clearInterval(this.intervalId)
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
  }
}
</script>

<style scoped>
/* Smooth tooltip transition */
.group:hover .group-hover\:visible {
  transition-delay: 100ms;
}

/* Arrow for tooltip */
.group-hover\:visible:before {
  content: '';
  position: absolute;
  top: -6px;
  left: 16px;
  width: 10px;
  height: 10px;
  background: white;
  border-left: 1px solid #e5e7eb;
  border-top: 1px solid #e5e7eb;
  transform: rotate(45deg);
}

/* Add these new styles */
.overflow-x-auto {
  max-height: calc(100vh - 300px);
  /* Adjust the value based on your header/footer heights */
  overflow-y: auto;
}

thead {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}
</style>